import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Copy, Download, RefreshCw, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { renderShareCard, resultShareText } from './shareCard';
import { trackEvent } from '../lib/analytics';

// Helper: build GA share parameters from the result data object.
// No PII is included — only score, accuracy and mode.
const shareParams = (data, method) => ({
  game_name: 'perfect_slice',
  game_mode: data?.mode,
  share_method: method,
  score: data?.best?.points,
  difference_percent: data?.best?.difference != null ? parseFloat(data.best.difference.toFixed(2)) : undefined,
  accuracy: data?.best?.difference != null ? parseFloat(Math.max(0, 100 - data.best.difference).toFixed(2)) : undefined,
});

export const ShareResultDialog = ({ data, onClose }) => {
  const [format, setFormat] = useState('square');
  const [renderedImage, setImage] = useState(null);
  const [error, setError] = useState('');
  const [fallback, setFallback] = useState('');
  const [revision, setRevision] = useState(0);
  const [sharing, setSharing] = useState(false);
  // Effects clear cached state after render. Never pair an old image with a
  // closed/replaced result during that intervening render (including its alt).
  const image = data?.best && renderedImage?.source === data && renderedImage.format === format && renderedImage.revision === revision ? renderedImage : null;
  const lifetime = useRef(0);
  useLayoutEffect(() => {
    lifetime.current += 1;
    setSharing(false);
    return () => { lifetime.current += 1; };
  }, [data]);
  const close = () => { lifetime.current += 1; onClose(); };
  useEffect(() => {
    let active = true, imageUrl;
    setImage(null); setError(''); setFallback('');
    if (data) renderShareCard(data, format).then(result => {
      if (!active) return;
      imageUrl = URL.createObjectURL(result.blob);
      setImage({ ...result, url: imageUrl, source: data, format, revision,
        alt: `Perfect Slice by CubitPlay — ${data.best.points} points, ${data.best.difference.toFixed(2)} percent off the target` });
    }).catch(err => { if (active) setError(err.message || 'Your image couldn’t be created. Try again.'); });
    return () => { active = false; if (imageUrl) URL.revokeObjectURL(imageUrl); };
  }, [data, format, revision]);
  const copyText = async () => {
    if (!data?.best) return;
    const generation = lifetime.current;
    const isCurrent = () => generation === lifetime.current;
    try {
      const text = resultShareText(data);
      try {
        await navigator.clipboard.writeText(text);
        if (isCurrent()) {
          toast('Result copied', { description: 'Ready to challenge a friend.' });
          // Clipboard write succeeded: the text is now in the user's clipboard.
          trackEvent('share', shareParams(data, 'copy_text'));
        }
      } catch {
        // Clipboard API blocked — show a fallback textarea for manual copy.
        // We do NOT fire the share event here: the user hasn't shared anything yet.
        if (isCurrent()) setFallback(text);
      }
    } catch { if (isCurrent()) setError('Your result could not be copied. Please reopen the share card.'); }
  };
  const download = () => {
    if (!image) return;
    const link = document.createElement('a'); link.href = image.url; link.download = image.filename;
    document.body.appendChild(link); link.click(); link.remove();
    toast('Image download started', { description: 'If your browser opens it, press and hold the image to save.' });
    trackEvent('share', shareParams(data, 'download_image'));
  };
  // On iOS / Android (canShare = true): opens native share sheet → user taps "Save Image" → Photos.
  // On desktop: falls back to direct download.
  const saveImage = async () => {
    if (!image || sharing) return;
    if (canShare) {
      const generation = lifetime.current;
      setSharing(true);
      try {
        await navigator.share({ title: 'Perfect Slice — by CubitPlay', files: [new File([image.blob], image.filename, { type: 'image/png' })] });
        if (generation === lifetime.current) trackEvent('share', shareParams(data, 'native_save'));
      } catch (err) {
        if (generation === lifetime.current && err?.name !== 'AbortError') download();
      } finally {
        if (generation === lifetime.current) setSharing(false);
      }
    } else {
      download();
    }
  };
  let canShare = false;
  if (image && typeof File !== 'undefined' && navigator.share && navigator.canShare) {
    try { canShare = navigator.canShare({ files: [new File([image.blob], image.filename, { type: 'image/png' })] }); } catch { /* Downloads and text work without native file sharing. */ }
  }
  const share = async () => {
    if (!image || sharing) return;
    const generation = lifetime.current;
    setSharing(true);
    try {
      await navigator.share({ title: 'Perfect Slice — by CubitPlay', text: image.text, files: [new File([image.blob], image.filename, { type: 'image/png' })] });
      // navigator.share resolves when the user completes the share action (not on cancel).
      if (generation === lifetime.current) trackEvent('share', shareParams(data, 'native'));
    }
    catch (err) { if (generation === lifetime.current && err.name !== 'AbortError') toast('Image sharing isn’t available here', { description: 'Use Save image or Copy text instead.' }); }
    finally { if (generation === lifetime.current) setSharing(false); }
  };
  return <Dialog open={!!data} onOpenChange={open => { if (!open) close(); }}><DialogContent className="ps-identity ps-share-dialog" data-testid="result-share-dialog"><DialogTitle data-testid="share-card-heading">A cut worth sharing.</DialogTitle><DialogDescription data-testid="share-card-description">Your best slice, ready to challenge a friend. No account needed.</DialogDescription>
    <div className="ps-share-format" aria-label="Image format">{[['square', 'Square'], ['landscape', 'Wide']].map(([value, label]) => <Button key={value} variant="ghost" data-testid={`share-format-${value}-button`} aria-pressed={format === value} onClick={() => setFormat(value)}>{label}</Button>)}</div>
    {image ? <img className="ps-share-card-preview" src={image.url} alt={image.alt} data-testid="result-share-preview-image"/> : error ? <div className="ps-share-status" role="alert" data-testid="share-card-error"><span>{error}</span><Button variant="ghost" data-testid="share-card-retry-button" onClick={() => setRevision(n => n + 1)} aria-label="Retry creating image"><RefreshCw size={17}/></Button></div> : <Skeleton className="ps-share-skeleton" data-testid="share-card-loading" aria-label="Preparing your share image"/>}
    <div className="ps-share-actions"><Button className="ps-share-primary" data-testid="result-share-download-png-button" disabled={!image || sharing} onClick={saveImage}><Download size={16}/> Save image</Button><Button variant="outline" className="ps-share-secondary" data-testid="result-share-copy-text-button" onClick={copyText} disabled={!data}><Copy size={16}/> Copy text</Button>{canShare && <Button variant="outline" className="ps-share-secondary col-span-2" data-testid="result-share-native-button" disabled={sharing} onClick={share}><Share2 size={16}/>{sharing ? 'Sharing…' : 'Share image'}</Button>}</div>
    {fallback && <div><p data-testid="share-card-fallback-notice">Copying is blocked in this browser. Select and copy your result below.</p><Textarea className="ps-share-fallback" data-testid="share-card-fallback-textarea" aria-label="Result text to copy" value={fallback} readOnly onFocus={event => event.target.select()}/></div>}
  </DialogContent></Dialog>;
};
